<template>
    <div class="flex items-center justify-between">
      <span class="text-lg font-semibold">
        Transcript Summary
        <span class="font-normal text-sm">(only newer transcript summaries will appear in new format)</span>
      </span>
      <div class="flex items-center gap-2">
        <button v-if="isJson" class="btn-primary-sm" @click="open_summary_edit=!open_summary_edit">
          Edit
        </button>
        <select
          v-if="transcript_options && transcript_options.length > 0"
          v-model="selected_transcript_option"
          class="border border-gray-300 rounded-md px-2 py-1 text-sm"
        >
          <option v-for="option in transcript_options" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>
    </div>
    <div class="sensitive mt-2 border rounded-md bg-blue-100 p-8 overflow-y-auto" style="max-height: 500px" >
      <!-- Dynamic Renderer: Use when a matching schema is found -->
      <DynamicRenderer
        v-if="currentDisplaySchema && isJson && jsonData && jsonData[selected_transcript_option]"
        :data="jsonData[selected_transcript_option]"
        :displaySchema="currentDisplaySchema"
        :showEntryHeaders="false"
      />

      <!-- Fallback: Hardcoded rendering for legacy summaries -->
      <div v-else-if="isJson && jsonData?.Raw">
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.items && jsonData[selected_transcript_option].items.length > 0">
          <ul class="font-normal text-sm list-disc ml-6 mt-2">
            <li v-for="(item, index) in jsonData[selected_transcript_option].items" :key="index" class="mb-2">
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
      <div v-else-if="isJson && jsonData && jsonData[selected_transcript_option]" class="">
        <!-- Overall Grades Section (Financial Performance Review) -->
        <div class="mt-2" v-if="jsonData[selected_transcript_option]?.overallGrades">
          <h2 class="font-medium text-md">Overall Performance Grades</h2>
          <div class="grid grid-cols-2 gap-4 mt-2 ml-6">
            <div v-if="jsonData[selected_transcript_option].overallGrades.qualityGrade">
              <span class="font-medium">Quality Grade:</span> {{ jsonData[selected_transcript_option].overallGrades.qualityGrade }}
            </div>
            <div v-if="jsonData[selected_transcript_option].overallGrades.utilizationGrade">
              <span class="font-medium">Utilization Grade:</span> {{ jsonData[selected_transcript_option].overallGrades.utilizationGrade }}
            </div>
            <div v-if="jsonData[selected_transcript_option].overallGrades.costGrade">
              <span class="font-medium">Cost Grade:</span> {{ jsonData[selected_transcript_option].overallGrades.costGrade }}
            </div>
            <div v-if="jsonData[selected_transcript_option].overallGrades.engagementGrade">
              <span class="font-medium">Engagement Grade:</span> {{ jsonData[selected_transcript_option].overallGrades.engagementGrade }}
            </div>
          </div>
        </div>

        <!-- ACO Financial Results Section -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.acoFinancialResults">
          <h2 class="font-medium text-md">ACO & Practice Financial Results</h2>
          <div class="ml-6 mt-2">
            <p v-if="jsonData[selected_transcript_option].acoFinancialResults.discussionPoints" class="text-sm mb-2">
              {{ jsonData[selected_transcript_option].acoFinancialResults.discussionPoints }}
            </p>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div v-if="jsonData[selected_transcript_option].acoFinancialResults.grossSavingsLosses !== undefined">
                <span class="font-medium">Gross Savings/Losses:</span> ${{ jsonData[selected_transcript_option].acoFinancialResults.grossSavingsLosses?.toLocaleString() || '0' }}
              </div>
              <div v-if="jsonData[selected_transcript_option].acoFinancialResults.sharedSavings !== undefined">
                <span class="font-medium">Shared Savings:</span> ${{ jsonData[selected_transcript_option].acoFinancialResults.sharedSavings?.toLocaleString() || '0' }}
              </div>
              <div v-if="jsonData[selected_transcript_option].acoFinancialResults.additionalSSPayment !== undefined">
                <span class="font-medium">Additional SS Payment:</span> ${{ jsonData[selected_transcript_option].acoFinancialResults.additionalSSPayment?.toLocaleString() || '0' }}
              </div>
              <div v-if="jsonData[selected_transcript_option].acoFinancialResults.totalPayment !== undefined">
                <span class="font-medium">Total Payment:</span> ${{ jsonData[selected_transcript_option].acoFinancialResults.totalPayment?.toLocaleString() || '0' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Claims Expenditures Overview Section -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.claimsExpendituresOverview &&
        Array.isArray(jsonData[selected_transcript_option].claimsExpendituresOverview) &&
        jsonData[selected_transcript_option].claimsExpendituresOverview.length > 0">
          <h2 class="font-medium text-md">Claims Expenditures Overview</h2>
          <div class="ml-6 mt-2">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b">
                  <th class="text-left py-2">Category</th>
                  <th class="text-right py-2">Actual</th>
                  <th class="text-right py-2">Target</th>
                  <th class="text-right py-2">Variance</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(claim, index) in jsonData[selected_transcript_option].claimsExpendituresOverview"
                    :key="index"
                    :class="{'font-medium border-t-2': claim.category === 'Total'}">
                  <td class="py-2">{{ claim.category }}</td>
                  <td class="text-right">${{ claim.actual?.toLocaleString() || '0' }}</td>
                  <td class="text-right">${{ claim.vhTarget?.toLocaleString() || '0' }}</td>
                  <td class="text-right" :class="{'text-red-600': claim.variance > 0, 'text-green-600': claim.variance < 0}">
                    ${{ claim.variance?.toLocaleString() || '0' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Claims Expenditures Areas Section -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.claimsExpendituresAreas &&
        Array.isArray(jsonData[selected_transcript_option].claimsExpendituresAreas) &&
        jsonData[selected_transcript_option].claimsExpendituresAreas.length > 0">
          <h2 class="font-medium text-md">Claims Expenditures Focus Areas</h2>
          <ul class="list-disc ml-6 mt-2">
            <li v-for="(area, index) in jsonData[selected_transcript_option].claimsExpendituresAreas" :key="index" class="mb-2">
              <span class="font-medium text-sm">{{ area.areaOfFocus }}:</span>
              <span class="text-sm"> {{ area.advisement }}</span>
            </li>
          </ul>
        </div>

        <!-- Practice-Specific Prescription Section -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.practiceSpecificPrescription">
          <h2 class="font-medium text-md">Practice-Specific Prescription</h2>
          <div class="ml-6 mt-2">
            <div v-if="jsonData[selected_transcript_option].practiceSpecificPrescription.recommendedOfferings &&
            Array.isArray(jsonData[selected_transcript_option].practiceSpecificPrescription.recommendedOfferings) &&
            jsonData[selected_transcript_option].practiceSpecificPrescription.recommendedOfferings.length > 0">
              <h3 class="font-medium text-sm">Recommended Offerings:</h3>
              <ul class="list-disc ml-6 text-sm mt-1">
                <li v-for="(offering, index) in jsonData[selected_transcript_option].practiceSpecificPrescription.recommendedOfferings" :key="index">
                  {{ offering }}
                </li>
              </ul>
            </div>
            <div v-if="jsonData[selected_transcript_option].practiceSpecificPrescription.notRecommendedOfferings &&
            Array.isArray(jsonData[selected_transcript_option].practiceSpecificPrescription.notRecommendedOfferings) &&
            jsonData[selected_transcript_option].practiceSpecificPrescription.notRecommendedOfferings.length > 0" class="mt-2">
              <h3 class="font-medium text-sm">Not Recommended:</h3>
              <ul class="list-disc ml-6 text-sm mt-1">
                <li v-for="(offering, index) in jsonData[selected_transcript_option].practiceSpecificPrescription.notRecommendedOfferings" :key="index">
                  {{ offering }}
                </li>
              </ul>
            </div>
            <div v-if="jsonData[selected_transcript_option].practiceSpecificPrescription.communityConsiderations" class="mt-2">
              <h3 class="font-medium text-sm">Community Considerations:</h3>
              <p class="text-sm mt-1">{{ jsonData[selected_transcript_option].practiceSpecificPrescription.communityConsiderations }}</p>
            </div>
          </div>
        </div>

        <!-- Flex Panel Section -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.flexPanel &&
        Array.isArray(jsonData[selected_transcript_option].flexPanel) &&
        jsonData[selected_transcript_option].flexPanel.length > 0">
          <h2 class="font-medium text-md">Additional Focus Areas</h2>
          <ul class="list-disc ml-6 text-sm mt-2">
            <li v-for="(item, index) in jsonData[selected_transcript_option].flexPanel" :key="index">
              {{ item }}
            </li>
          </ul>
        </div>

        <!-- Financial Statement Section -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.financialStatement">
          <h2 class="font-medium text-md">Financial Impact Statement</h2>
          <div class="ml-6 mt-2 text-sm">
            <div v-if="jsonData[selected_transcript_option].financialStatement.totalFinancialImpact !== undefined">
              <span class="font-medium">Total Financial Impact:</span>
              ${{ jsonData[selected_transcript_option].financialStatement.totalFinancialImpact?.toLocaleString() || '0' }}
            </div>
            <div v-if="jsonData[selected_transcript_option].financialStatement.missedOpportunities" class="mt-1">
              <span class="font-medium">Missed Opportunities:</span>
              {{ jsonData[selected_transcript_option].financialStatement.missedOpportunities }}
            </div>
          </div>
        </div>

        <!-- Call to Action Section -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.callToAction &&
        Array.isArray(jsonData[selected_transcript_option].callToAction) &&
        jsonData[selected_transcript_option].callToAction.length > 0">
          <h2 class="font-medium text-md">Call to Action</h2>
          <div v-for="(action, index) in jsonData[selected_transcript_option].callToAction" :key="index"
               class="ml-6 mt-3 p-3 border rounded-md bg-white">
            <h3 class="font-medium text-sm">{{ action.areaOfFocus }}</h3>
            <div class="mt-2 text-sm space-y-1">
              <p><span class="font-medium">Why:</span> {{ action.why }}</p>
              <p><span class="font-medium">Current Performance:</span> {{ action.currentPerformance }}</p>
              <p><span class="font-medium">Goal:</span> {{ action.goal }}</p>
              <p><span class="font-medium">Opportunity:</span> {{ action.opportunity }}</p>
              <div v-if="action.owners">
                <span class="font-medium">Owners:</span>
                <span v-if="action.owners.vhPerson"> Team: {{ action.owners.vhPerson }}</span>
                <span v-if="action.owners.practicePerson"> | Practice: {{ action.owners.practicePerson }}</span>
              </div>
              <div v-if="action.resourceNeeds && Array.isArray(action.resourceNeeds) && action.resourceNeeds.length > 0">
                <span class="font-medium">Resource Needs:</span>
                <ul class="list-disc ml-6">
                  <li v-for="(resource, rIndex) in action.resourceNeeds" :key="rIndex">{{ resource }}</li>
                </ul>
              </div>
              <p v-if="action.notes"><span class="font-medium">Notes:</span> {{ action.notes }}</p>
            </div>
          </div>
        </div>

        <!-- Next Financial Performance Review -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.nextFinancialPerformanceReview">
          <h2 class="font-medium text-md">Next Financial Performance Review</h2>
          <p class="ml-6 text-sm mt-2">{{ new Date(jsonData[selected_transcript_option].nextFinancialPerformanceReview).toLocaleString() }}</p>
        </div>

        <!-- Previous Action Items Section -->
        <div class="mt-2" v-if="jsonData[selected_transcript_option]?.previousActionItems &&
        Array.isArray(jsonData[selected_transcript_option].previousActionItems) &&
        jsonData[selected_transcript_option].previousActionItems.length > 0">
          <h2 class="font-medium">Previous Action Items</h2>
          <ul class="font-normal text-sm list-disc ml-6 mt-2">
            <li v-for="(item, index) in jsonData[selected_transcript_option].previousActionItems" :key="index">{{ item }}</li>
          </ul>
        </div>

        <!-- Performance Report Review Section -->
        <div class="mt-2" v-if="jsonData[selected_transcript_option]?.performanceReportReview &&
        Array.isArray(jsonData[selected_transcript_option].performanceReportReview) &&
        jsonData[selected_transcript_option].performanceReportReview.length > 0">
          <h2 class="font-medium text-md">Performance Report Review</h2>
          <ul v-for="(report, reportIndex) in jsonData[selected_transcript_option].performanceReportReview" :key="reportIndex" class=" mt-4 ml-6">
            <li class="font-medium list-disc text-sm">
              {{ report.category }}

              <div class="font-normal text-sm mt-2 mb-4" v-if="report.details">
                <p v-if="report.details.issue"><strong>Issue:</strong> {{ report.details.issue }}</p>
                <div v-if="report.details.patientsDiscussed && Array.isArray(report.details.patientsDiscussed) && report.details.patientsDiscussed.length > 0">
                  <h4 class="font-medium text-sm">Patients Discussed:</h4>
                  <ul v-for="(patient, patientIndex) in report.details.patientsDiscussed" :key="patientIndex" class="list-disc ml-6 text-sm ">
                    <li>
                      <p class="sensitive"><strong>Name:</strong> {{ patient.first_name }} {{ patient.last_name }}</p>
                      <p v-if="patient.beneficiary_mbi" class="sensitive"><strong>MBI:</strong> {{ patient.beneficiary_mbi }}</p>
                      <p v-if="patient.clinicalDetail"><strong>Clinical Details:</strong> {{ patient.clinicalDetail }}</p>
                    </li>
                  </ul>
                </div>

                <p v-if="report.details.barriers"><strong>Barriers:</strong> {{ report.details.barriers }}</p>
                <p v-if="report.details.strategies"><strong>Strategies:</strong> {{ report.details.strategies }}</p>

                <div v-if="report.details.resources && Array.isArray(report.details.resources) && report.details.resources.length > 0">
                  <h4 class="font-medium text-sm">Resources:</h4>
                  <ul class="list-disc ml-6 text-sm ">
                    <li v-for="(resource, resourceIndex) in report.details.resources" :key="resourceIndex">
                      {{ resource }}
                    </li>
                  </ul>
                </div>

                <div v-if="report?.details?.nextSteps && Array.isArray(report.details.nextSteps) && report.details.nextSteps.length > 0"><strong>Next Steps:</strong>
                  <ul class="list-disc ml-6 text-sm ">
                    <li v-for="next_step in report.details.nextSteps" :key="next_step">
                      {{ next_step.task }}
                    </li>
                  </ul>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Data Reviewed Section (PTM schema) -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.dataReviewed &&
        Array.isArray(jsonData[selected_transcript_option].dataReviewed) &&
        jsonData[selected_transcript_option].dataReviewed.length > 0">
          <h2 class="font-medium text-md">Data Reviewed</h2>
          <ul v-for="(data, index) in jsonData[selected_transcript_option].dataReviewed" :key="index" class="list-disc ml-6 text-sm mt-2">
            <li>
              <h3 class="font-medium">{{ data.source }}</h3>
              <p v-if="data.details">{{ data.details }}</p>
            </li>
          </ul>
        </div>

        <!-- Practice Wins Section (PTM schema) -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.practiceWins &&
        Array.isArray(jsonData[selected_transcript_option].practiceWins) &&
        jsonData[selected_transcript_option].practiceWins.length > 0">
          <h2 class="font-medium text-md">Practice Wins</h2>
          <ul class="list-disc ml-6 text-sm mt-2">
            <li v-for="(win, index) in jsonData[selected_transcript_option].practiceWins" :key="index">{{ win }}</li>
          </ul>
        </div>

        <!-- Practice Barriers Section (PTM schema) -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.practiceBarriers &&
        Array.isArray(jsonData[selected_transcript_option].practiceBarriers) &&
        jsonData[selected_transcript_option].practiceBarriers.length > 0">
          <h2 class="font-medium text-md">Practice Barriers</h2>
          <ul class="list-disc ml-6 text-sm mt-2">
            <li v-for="(barrier, index) in jsonData[selected_transcript_option].practiceBarriers" :key="index">{{ barrier }}</li>
          </ul>
        </div>

        <!-- Patient Case Review Section -->
        <div class="mt-4" v-if="jsonData[selected_transcript_option]?.patientCaseReview?.patients &&
        Array.isArray(jsonData[selected_transcript_option].patientCaseReview.patients) &&
        jsonData[selected_transcript_option].patientCaseReview.patients.length > 0">
          <h2 class="font-medium text-md">Patient Case Review</h2>
          <ul v-for="(patient, patientIndex) in jsonData[selected_transcript_option].patientCaseReview.patients"
              :key="patientIndex" class="list-disc ml-6 text-sm mt-2">
            <li class="text-sm">
              <div v-if="patient.patientsDiscussed && Array.isArray(patient.patientsDiscussed) && patient.patientsDiscussed.length > 0">
                <h3>Patients Discussed:</h3>
                <div v-for="(p, pIndex) in patient.patientsDiscussed" :key="pIndex" class="patient-card">
                  <p class="sensitive"><strong>Name:</strong> {{ p.first_name }} {{ p.last_name }}</p>
                  <p v-if="p.beneficiary_mbi" class="sensitive"><strong>MBI:</strong> {{ p.beneficiary_mbi }}</p>
                  <p v-if="p.clinicalDetail"><strong>Clinical Details:</strong> {{ p.clinicalDetail }}</p>
                </div>
              </div>

              <p v-if="patient.issue"><strong>Issue:</strong> {{ patient.issue }}</p>
              <p v-if="patient.barriers"><strong>Barriers:</strong> {{ patient.barriers }}</p>
              <p v-if="patient.strategies"><strong>Strategies:</strong> {{ patient.strategies }}</p>

              <div v-if="patient.resources && Array.isArray(patient.resources) && patient.resources.length > 0">
                <h4>Resources:</h4>
                <ul>
                  <li v-for="(resource, resourceIndex) in patient.resources" :key="resourceIndex">{{ resource }}</li>
                </ul>
              </div>

              <div v-if="patient?.details?.nextSteps && Array.isArray(patient.details.nextSteps) && patient.details.nextSteps.length > 0"><strong>Next Steps:</strong>
                <ul class="list-disc ml-6 text-sm ">
                  <li v-for="next_step in patient.details.nextSteps" :key="next_step">
                    {{ next_step.task }}
                  </li>
                </ul>
              </div>
            </li>

          </ul>
        </div>

        <!-- Follow-Up & Next Steps Section -->
        <div class="" v-if="jsonData[selected_transcript_option]?.followUpNextSteps">
          <h2 class="font-medium text-md">Follow-Up & Next Steps</h2>

          <div v-if="jsonData[selected_transcript_option].followUpNextSteps.providerTasks &&
          Array.isArray(jsonData[selected_transcript_option].followUpNextSteps.providerTasks) &&
          jsonData[selected_transcript_option].followUpNextSteps.providerTasks.length > 0">
            <h3 class="font-medium text-sm mt-4">Provider Tasks:</h3>
            <ul class="font-normal text-sm list-disc ml-6 mt-2">
              <li class="" v-for="(task, index) in jsonData[selected_transcript_option].followUpNextSteps.providerTasks" :key="index">{{ task.task }}</li>
            </ul>
          </div>

          <div v-if="jsonData[selected_transcript_option].followUpNextSteps.medicalDirectorTasks &&
          Array.isArray(jsonData[selected_transcript_option].followUpNextSteps.medicalDirectorTasks) &&
          jsonData[selected_transcript_option].followUpNextSteps.medicalDirectorTasks.length > 0">
            <h3 class="font-medium text-sm mt-4">Regional Medical Officer Tasks:</h3>
            <ul class="font-normal text-sm list-disc ml-6 mt-2">
              <li v-for="(task, index) in jsonData[selected_transcript_option].followUpNextSteps.medicalDirectorTasks"
                  :key="index">{{ task.task }}</li>
            </ul>
          </div>

          <div v-if="jsonData[selected_transcript_option].followUpNextSteps.practiceQuestions &&
          Array.isArray(jsonData[selected_transcript_option].followUpNextSteps.practiceQuestions) &&
          jsonData[selected_transcript_option].followUpNextSteps.practiceQuestions.length > 0">
            <h3 class="font-medium text-sm mt-4">Practice Questions:</h3>
            <ul class="font-normal text-sm list-disc ml-6 mt-2">
              <li v-for="(question, index) in jsonData[selected_transcript_option].followUpNextSteps.practiceQuestions" :key="index">{{ question.task }}</li>
            </ul>
          </div>

          <div v-if="jsonData[selected_transcript_option].followUpNextSteps.resourcesGiven &&
          Array.isArray(jsonData[selected_transcript_option].followUpNextSteps.resourcesGiven) &&
          jsonData[selected_transcript_option].followUpNextSteps.resourcesGiven.length > 0">
            <h3 class="font-medium text-sm mt-4">Resources Given:</h3>
            <ul class="font-normal text-sm list-disc ml-6 mt-2">
              <li v-for="(resource, index) in jsonData[selected_transcript_option].followUpNextSteps.resourcesGiven" :key="index">
                <a :href="resource" target="_blank">{{ resource }}</a>
              </li>
            </ul>
          </div>

          <div class="font-medium text-md mt-4" v-if="jsonData[selected_transcript_option].followUpNextSteps.nextMeetingDateTime">
            <div class="font-normal"><span class="font-medium" >Next Meeting: </span>{{ jsonData[selected_transcript_option].followUpNextSteps.nextMeetingDateTime }}</div>
          </div>
        </div>

        <!-- New Action Items Section (PTM schema) -->
        <div class="mt-4 font-medium" v-if="jsonData[selected_transcript_option]?.newActionItems">
          <h2>New Action Items</h2>

          <div v-if="jsonData[selected_transcript_option].newActionItems.vhTeamTasks &&
          Array.isArray(jsonData[selected_transcript_option].newActionItems.vhTeamTasks) &&
          jsonData[selected_transcript_option].newActionItems.vhTeamTasks.length > 0">
            <h3 class="font-medium text-sm mt-4 italic">Team Tasks:</h3>
            <ul class="font-normal text-sm list-disc ml-6 mt-2">
              <li v-for="(task, index) in jsonData[selected_transcript_option].newActionItems.vhTeamTasks" :key="index">{{ task }}</li>
            </ul>
          </div>

          <div v-if="jsonData[selected_transcript_option].newActionItems.practiceTeamTasks &&
          Array.isArray(jsonData[selected_transcript_option].newActionItems.practiceTeamTasks) &&
          jsonData[selected_transcript_option].newActionItems.practiceTeamTasks.length > 0">
            <h3 class="font-medium text-sm mt-4 italic">Practice Team Tasks:</h3>
            <ul class="font-normal text-sm list-disc ml-6 mt-2">
              <li v-for="(task, index) in jsonData[selected_transcript_option].newActionItems.practiceTeamTasks" :key="index">{{ task }}</li>
            </ul>
          </div>
        </div>

        <!-- Practice Questions Section (PTM schema) -->
        <div class="mt-4 font-medium" v-if="jsonData[selected_transcript_option]?.practiceQuestions &&
        Array.isArray(jsonData[selected_transcript_option].practiceQuestions) &&
        jsonData[selected_transcript_option].practiceQuestions.length > 0">
          <h3 class="font-medium text-sm mt-4 italic">Practice Questions</h3>
            <ul class="font-normal text-sm list-disc ml-6 mt-2">
              <li v-for="(question, index) in jsonData[selected_transcript_option].practiceQuestions" :key="index">
                {{ question }}
              </li>
          </ul>
        </div>

        <!-- Next Meeting Section (PTM schema) -->
        <div class="mt-4 font-medium" v-if="jsonData[selected_transcript_option]?.nextMeetingDate">
          <h2>Next Meeting</h2>
          <p class="font-normal text-sm mt-2"><strong>Date & Time:</strong> {{ jsonData[selected_transcript_option].nextMeetingDate }}</p>
        </div>
      </div>

      <div v-else class="">
        {{transcriptSummary}}
      </div>
    </div>

  <JsonEditor v-if="open_summary_edit"
              :initial-data="transcriptSummary ? JSON.parse(transcriptSummary) : {}"
              @close="open_summary_edit = false"
              @save="save_new_summary"
  />

</template>

<script>
import JsonEditor from "@/components/widgetcomponents/JSON_Editors/JSON_Editor.vue";
import DynamicRenderer from "@/components/widgetcomponents/JSON_Editors/DynamicRenderer.vue";
import axios from "axios";

export default {
  name: 'Transcript_Summary',
  components: {JsonEditor, DynamicRenderer},
  props: {
    transcriptSummary:{
      type: String,
      required: true
    }
  },
  emits: ['update'],
  data() {
    return {
      jsonData: {},
      transcript_options:[],
      isJson:false,
      selected_transcript_option: null,
      open_summary_edit:false,
      schemas: [],
      schemasLoaded: false
    }
  },
  computed: {
    // Find the matching schema for the currently selected transcript option
    currentDisplaySchema() {
      if (!this.selected_transcript_option || !this.schemas.length) {
        return null;
      }
      // Match by name (case-insensitive). A retired schema can share a name
      // with a live one, so prefer the active record.
      const selected = this.selected_transcript_option.toLowerCase();
      const matches = this.schemas.filter(s => s.name.toLowerCase() === selected);
      const schema = matches.find(s => s.is_active) || matches[0];
      return schema?.display_schema || null;
    }
  },
  methods: {
    parse_json_summary() {
      try {
        // First check if transcriptSummary is already an object
        if (typeof this.transcriptSummary === 'object' && this.transcriptSummary !== null) {
          this.jsonData = this.transcriptSummary;
          this.transcript_options = Object.keys(this.jsonData);

          // Set the first option as selected by default if available
          if (this.transcript_options.length > 0) {
            this.selected_transcript_option = this.transcript_options[0];
          }

          this.isJson = true;
        } else {
          // If it's a string, try to parse it as JSON
          this.jsonData = JSON.parse(this.transcriptSummary);
          this.transcript_options = Object.keys(this.jsonData);

          // Set the first option as selected by default if available
          if (this.transcript_options.length > 0) {
            this.selected_transcript_option = this.transcript_options[0];
          }

          this.isJson = true;
        }
      } catch (error) {
        console.log("Could not parse transcription summary as JSON:", error.message);

        // Handle raw text by parsing numbered items
        try {
          const rawText = this.transcriptSummary.trim();
          // Match patterns like "1.", "2.", "3." followed by text
          const numberedPattern = /(\d+\.\s*)([^1-9]*?)(?=\n\d+\.|$)/gs;
          const matches = [...rawText.matchAll(numberedPattern)];

          if (matches.length > 0) {
            const items = matches.map(match => match[2].trim());
            this.jsonData = {"Raw": {"items": items}};
            this.transcript_options = ["Raw"];
            this.selected_transcript_option = "Raw";
            this.isJson = true;
          } else {
            // If no numbered items found, just display as plain text
            this.isJson = false;
          }
        } catch (textParseError) {
          console.log("Error parsing numbered list:", textParseError.message);
          this.isJson = false;
        }
      }
    },
    save_new_summary(new_summary) {
      this.$emit('update', new_summary);
      this.open_summary_edit = false;
    },
    async fetchSchemas() {
      // No is_active filter: a deactivated schema still supplies the display
      // schema for summaries generated while it was live.
      try {
        const response = await axios.get('/api/transcript-summary-schemas/');
        this.schemas = response.data.results || response.data || [];
      } catch (error) {
        console.log('Could not fetch transcript summary schemas:', error.message);
      } finally {
        this.schemasLoaded = true;
        this.selectPreferredOption();
      }
    },
    selectPreferredOption() {
      // Open on the tenant's default schema when this summary has a section
      // for it. Tenants with no default keep the first key.
      const defaultSchema = this.schemas.find(s => s.is_default);
      if (!defaultSchema) {
        return;
      }
      const match = this.transcript_options.find(
        option => option.toLowerCase() === defaultSchema.name.toLowerCase()
      );
      if (match) {
        this.selected_transcript_option = match;
      }
    }
  },
  mounted() {
    this.parse_json_summary();
    this.fetchSchemas();
  },
}
</script>

<style scoped>

</style>
